/**
 * Quorums (S-17, spec §5) — ask several models one question at once.
 *
 * A quorum is just a name and a set of models. You pass a brief; each model
 * answers it independently, and you get every answer back. Optionally the
 * quorum combines them: one model you pick reads all the answers under a
 * system prompt you write, and returns a single merged answer. No verdict
 * vocabulary, no scoring — the question is whatever the brief says.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { Button, Flash } from '@primer/react';
import { PlusIcon, TrashIcon } from '@primer/octicons-react';

import { useQuorums, useQuorumRuns, useHarnesses, useConsoleMutation } from '../queries.js';
import { useStore } from '../store-context.js';
import { QueryPhase, Section, Chip } from '../components/state.js';

interface ParticipantForm {
  harness: string;
  model: string;
}

interface QuorumForm {
  participants: ParticipantForm[];
  combine: boolean;
  combinerHarness: string;
  combinerModel: string;
  combinerPrompt: string;
}

function blankForm(defaultHarness: string): QuorumForm {
  return {
    participants: [
      { harness: defaultHarness, model: '' },
      { harness: defaultHarness, model: '' },
    ],
    combine: false,
    combinerHarness: defaultHarness,
    combinerModel: '',
    combinerPrompt: '',
  };
}

/** The form → the definition the host reads. Empty model means account default. */
function toDefinition(form: QuorumForm): Record<string, unknown> {
  const definition: Record<string, unknown> = {
    participants: form.participants.map((participant) => ({
      harness: participant.harness,
      model: participant.model.trim().length === 0 ? null : participant.model.trim(),
    })),
  };
  if (form.combine) {
    definition['combiner'] = {
      harness: form.combinerHarness,
      model: form.combinerModel.trim().length === 0 ? null : form.combinerModel.trim(),
      prompt: form.combinerPrompt,
    };
  }
  return definition;
}

/** A stored definition → the form, so «New version» opens on what runs now. */
function fromDefinition(json: string, defaultHarness: string): QuorumForm {
  try {
    const parsed = JSON.parse(json) as Record<string, unknown>;
    const participants = Array.isArray(parsed['participants']) ? (parsed['participants'] as Record<string, unknown>[]) : [];
    const combiner = (parsed['combiner'] ?? null) as Record<string, unknown> | null;
    const base = blankForm(defaultHarness);
    return {
      participants:
        participants.length > 0
          ? participants.map((participant) => ({
              harness: typeof participant['harness'] === 'string' ? participant['harness'] : defaultHarness,
              model: typeof participant['model'] === 'string' ? participant['model'] : '',
            }))
          : base.participants,
      combine: combiner !== null,
      combinerHarness: typeof combiner?.['harness'] === 'string' ? (combiner['harness'] as string) : defaultHarness,
      combinerModel: typeof combiner?.['model'] === 'string' ? (combiner['model'] as string) : '',
      combinerPrompt: typeof combiner?.['prompt'] === 'string' ? (combiner['prompt'] as string) : '',
    };
  } catch {
    return blankForm(defaultHarness);
  }
}

export function QuorumsScreen(): ReactNode {
  const quorums = useQuorums();
  const runs = useQuorumRuns();
  const harnesses = useHarnesses();

  const harnessList = harnesses.data ?? [];
  const defaultHarness = harnessList[0]?.id ?? 'codex';

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const create = useConsoleMutation<{ slug: string; name: string }, void>(
    (data, context, input) => data.createQuorum(input.slug, input.name, context),
    [['quorums']],
  );
  // After a create, open the configurator for the fresh quorum straight away —
  // creating an empty shell and leaving it unconfigured is the ceremony to cut.
  const [justCreated, setJustCreated] = useState<string | null>(null);
  useEffect(() => {
    if (justCreated === null) return;
    const match = (quorums.data ?? []).find((quorum) => quorum.slug === justCreated);
    if (match !== undefined) {
      setEditing(match.id);
      setJustCreated(null);
    }
  }, [justCreated, quorums.data]);
  const activate = useConsoleMutation<{ id: string; version: number }, void>(
    (data, context, input) => data.activateQuorumVersion(input.id, input.version, context),
    [['quorums']],
  );

  return (
    <div className="stack">
      <Section
        label="Quorums"
        lede="Ask several models one question at once. A role references a quorum by slug and passes the brief; each model answers on its own, and you get every answer — or one combined answer."
      >
        <div className="quorum-create">
          <input placeholder="slug (e.g. reviewers)" value={slug} onChange={(event) => setSlug(event.target.value)} />
          <input placeholder="Display name" value={name} onChange={(event) => setName(event.target.value)} />
          <Button
            disabled={slug.trim().length === 0 || create.isPending}
            onClick={() => {
              const chosen = slug.trim();
              create.mutate({ slug: chosen, name: name.trim().length === 0 ? chosen : name.trim() });
              setJustCreated(chosen);
              setSlug('');
              setName('');
            }}
          >
            Create quorum
          </Button>
        </div>

        {quorums.isPending || quorums.error !== null || (quorums.data?.length ?? 0) === 0 ? (
          <QueryPhase
            isPending={quorums.isPending}
            error={quorums.error}
            isEmpty={quorums.data?.length === 0}
            source="quorums"
            emptyTitle="No quorums yet"
            emptyDetail="Create one, then add its models. Call it from a role by its slug."
            onRetry={() => void quorums.refetch()}
            rows={2}
          />
        ) : (
          <ul className="quorum-list">
            {(quorums.data ?? []).map((quorum) => (
              <li className="quorum-item" key={quorum.id}>
                <div className="quorum-head">
                  <div>
                    <strong>quorum:{quorum.slug}</strong> <span className="quorum-name">{quorum.displayName}</span>
                  </div>
                  {quorum.activeVersion === null ? (
                    <Chip tone="wait" text="no active version" />
                  ) : (
                    <Chip tone="active" text={`v${String(quorum.activeVersion)} active`} />
                  )}
                </div>
                <div className="quorum-versions">
                  {quorum.versions.length === 0 ? <span>no versions yet</span> : null}
                  {quorum.versions.map((version) => (
                    <span className="quorum-version" key={version.version}>
                      v{version.version} · {version.state}
                      {version.state !== 'active' ? (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => activate.mutate({ id: quorum.id, version: version.version })}
                        >
                          activate
                        </button>
                      ) : null}
                    </span>
                  ))}
                </div>
                <Button size="small" onClick={() => setEditing(editing === quorum.id ? null : quorum.id)}>
                  {editing === quorum.id ? 'Close' : quorum.versions.length === 0 ? 'Configure first version' : 'New version'}
                </Button>
                {editing === quorum.id ? (
                  <QuorumEditor
                    quorumId={quorum.id}
                    harnesses={harnessList}
                    defaultHarness={defaultHarness}
                    onDone={() => setEditing(null)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section label="Recent runs">
        {runs.isPending || runs.error !== null || (runs.data?.length ?? 0) === 0 ? (
          <QueryPhase
            isPending={runs.isPending}
            error={runs.error}
            isEmpty={runs.data?.length === 0}
            source="quorum runs"
            emptyTitle="No runs yet"
            emptyDetail="A run appears when a role convenes a quorum."
            onRetry={() => void runs.refetch()}
            rows={2}
          />
        ) : (
          <ul className="quorum-run-list">
            {(runs.data ?? []).map((run) => {
              const combined = combinedOf(run.aggregate);
              return (
                <li className="quorum-run" key={run.id}>
                  <div className="quorum-run-head">
                    <Chip tone={run.state === 'COMPLETED' ? 'active' : run.state === 'STOPPED' ? 'wait' : 'danger'} text={run.state} />
                    <span>v{run.quorumVersion}</span>
                    <span className="quorum-run-when">{new Date(run.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="quorum-run-members">
                    {run.participants.map((participant, index) => (
                      <span className="quorum-member" key={index}>
                        {participant.harness}
                        {participant.verdict === null ? ' · —' : ' · answered'}
                      </span>
                    ))}
                  </div>
                  {combined === null ? null : (
                    <Flash>
                      <strong>Combined:</strong> {combined.length > 400 ? `${combined.slice(0, 400)}…` : combined}
                    </Flash>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}

/** A model picker for one harness: the account's live list, plus «other…». */
function ModelSelect({
  harnesses,
  harness,
  model,
  onChange,
}: {
  harnesses: Awaited<ReturnType<import('../../client/data-source.js').ConsoleDataSource['harnesses']>>;
  harness: string;
  model: string;
  onChange: (model: string) => void;
}): ReactNode {
  const models = harnesses.find((entry) => entry.id === harness)?.models ?? [];
  const known = model.length === 0 || models.some((entry) => entry.id === model);
  return (
    <>
      <select
        value={known ? model : '__custom__'}
        onChange={(event) => {
          if (event.target.value === '__custom__') {
            onChange(model.length === 0 ? ' ' : model);
            return;
          }
          onChange(event.target.value);
        }}
      >
        <option value="">account default</option>
        {models.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.label}
          </option>
        ))}
        <option value="__custom__">other…</option>
      </select>
      {known ? null : (
        <input
          className="quorum-model-custom"
          value={model.trim()}
          placeholder="exact model id"
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </>
  );
}

function QuorumEditor({
  quorumId,
  harnesses,
  defaultHarness,
  onDone,
}: {
  quorumId: string;
  harnesses: Awaited<ReturnType<import('../../client/data-source.js').ConsoleDataSource['harnesses']>>;
  defaultHarness: string;
  onDone: () => void;
}): ReactNode {
  const store = useStore();
  const [form, setForm] = useState<QuorumForm>(() => blankForm(defaultHarness));

  // Open on the active version's configuration when there is one.
  useEffect(() => {
    let cancelled = false;
    void store.data.quorumVersions(quorumId).then((versions) => {
      const active = versions.find((version) => version.state === 'active') ?? versions[0];
      if (!cancelled && active !== undefined) setForm(fromDefinition(active.definition, defaultHarness));
    });
    return () => {
      cancelled = true;
    };
  }, [quorumId, defaultHarness, store]);

  // «Save & activate» is one action: draft the version, then make it live —
  // the common case needs no second click. «Save as draft» stays for staging.
  const save = useConsoleMutation<{ activate: boolean }, void>(
    async (data, context, input) => {
      const version = await data.draftQuorumVersion(quorumId, JSON.stringify(toDefinition(form)), 'edited in the Console', context);
      if (input.activate) await data.activateQuorumVersion(quorumId, version, context);
    },
    [['quorums']],
  );

  const setParticipant = (index: number, patch: Partial<ParticipantForm>): void =>
    setForm({ ...form, participants: form.participants.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)) });

  return (
    <div className="quorum-editor">
      <div className="quorum-field-label">Models</div>
      <table className="quorum-participants">
        <thead>
          <tr>
            <th>Program</th>
            <th>Model</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {form.participants.map((participant, index) => (
            <tr key={index}>
              <td>
                <select value={participant.harness} onChange={(event) => setParticipant(index, { harness: event.target.value, model: '' })}>
                  {harnesses.map((harness) => (
                    <option key={harness.id} value={harness.id}>
                      {harness.label}
                    </option>
                  ))}
                </select>
              </td>
              <td className="quorum-model-cell">
                <ModelSelect harnesses={harnesses} harness={participant.harness} model={participant.model} onChange={(model) => setParticipant(index, { model })} />
              </td>
              <td>
                {form.participants.length > 1 ? (
                  <Button size="small" variant="danger" leadingVisual={TrashIcon} onClick={() => setForm({ ...form, participants: form.participants.filter((entry, at) => at !== index) })}>
                    <span className="sr-only">Remove</span>
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        size="small"
        leadingVisual={PlusIcon}
        onClick={() => setForm({ ...form, participants: [...form.participants, { harness: defaultHarness, model: '' }] })}
      >
        Add model
      </Button>

      <label className="check quorum-combine-toggle">
        <input type="checkbox" checked={form.combine} onChange={(event) => setForm({ ...form, combine: event.target.checked })} />
        Combine the answers into one
      </label>
      <p className="hint">
        {form.combine
          ? 'A model you pick reads every answer and returns one merged answer, guided by the prompt below.'
          : 'Off: the caller gets each model’s answer, as it gave it.'}
      </p>

      {form.combine ? (
        <div className="quorum-combiner">
          <div className="quorum-field-label">Combined by</div>
          <div className="quorum-judge-row">
            <select value={form.combinerHarness} onChange={(event) => setForm({ ...form, combinerHarness: event.target.value, combinerModel: '' })}>
              {harnesses.map((harness) => (
                <option key={harness.id} value={harness.id}>
                  {harness.label}
                </option>
              ))}
            </select>
            <ModelSelect harnesses={harnesses} harness={form.combinerHarness} model={form.combinerModel} onChange={(combinerModel) => setForm({ ...form, combinerModel })} />
          </div>
          <div className="quorum-field-label">How to combine (system prompt)</div>
          <textarea
            rows={4}
            value={form.combinerPrompt}
            placeholder="e.g. Read the answers below and write one recommendation, noting where they agree and disagree."
            onChange={(event) => setForm({ ...form, combinerPrompt: event.target.value })}
          />
        </div>
      ) : null}

      <div className="quorum-editor-actions">
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate({ activate: true }, { onSuccess: onDone })}>
          Save &amp; activate
        </Button>
        <Button disabled={save.isPending} onClick={() => save.mutate({ activate: false }, { onSuccess: onDone })}>
          Save as draft
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

/** The combined answer out of a run's stored aggregate, or null when not combined. */
function combinedOf(aggregate: string | null): string | null {
  if (aggregate === null) return null;
  try {
    const parsed = JSON.parse(aggregate) as { combined?: unknown };
    return typeof parsed.combined === 'string' && parsed.combined.length > 0 ? parsed.combined : null;
  } catch {
    return null;
  }
}
